import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Button,
  Alert,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import TableChartIcon from '@mui/icons-material/TableChart';
import * as XLSX from 'xlsx';
import { useNavigate, useLocation } from 'react-router-dom';
import accountsApiService from '../../services/accountsApiService';
import { decryptText } from '../../utils/encryption';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/reactApi/api';

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const [year, month, day] = dateString.split('-');
  return `${day}/${month}/${year}`;
};

const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return '₹ 0.00';
  return `₹ ${Number(amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const LedgerPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloadingInvoice, setDownloadingInvoice] = useState(null);
  const [downloadingReceipt, setDownloadingReceipt] = useState(null);

  const urlusername = new URLSearchParams(location.search).get('username') || localStorage.getItem('username') || '';
  const username = decryptText(urlusername);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!username) {
          setError('User not logged in');
          setLoading(false);
          return;
        }
        const response = await accountsApiService.getLedgerEntries(username);
        setData(response.value || []);
      } catch (err) {
        setError(err.message || 'Failed to fetch ledger entries');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [username]);

  const handleDownloadInvoice = async (documentNo) => {
    try {
      setDownloadingInvoice(documentNo);
      const url = `${API_BASE_URL}/accounts/business-central/invoice?invoiceNo=${encodeURIComponent(documentNo)}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error('Invoice not available');
      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = `Invoice_${documentNo.replace(/[^a-zA-Z0-9]/g, '-')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
    } catch (err) {
      alert('Failed to download invoice: ' + err.message);
    } finally {
      setDownloadingInvoice(null);
    }
  };

  const handleDownloadReceipt = async (documentNo) => {
    try {
      setDownloadingReceipt(documentNo);
      const url = `${API_BASE_URL}/accounts/business-central/receipt?documentNo=${encodeURIComponent(documentNo)}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error('Receipt not available');
      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = `Receipt_${documentNo.replace(/[^a-zA-Z0-9]/g, '-')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
    } catch (err) {
      alert('Failed to download receipt: ' + err.message);
    } finally {
      setDownloadingReceipt(null);
    }
  };

  const currentBalance = data.length > 0 ? data[data.length - 1].runningBalance : 0;

  const handleExportExcel = () => {
    const wsData = [
      ['S.No', 'Document No', 'Posting Date', 'Customer Name', 'Lab ID', 'Document Type', 'Debit (₹)', 'Credit (₹)', 'Running Balance (₹)', 'Overdue Days'],
      ...data.map((row, index) => {
        const isInvoice = row.documentType === 'Invoice';
        const isReceipt = (row.documentNo || '').startsWith('RV/');
        return [
          index + 1,
          row.documentNo || '',
          formatDate(row.postingDate),
          row.customerName || '',
          row.labId || '',
          isInvoice ? 'Invoice' : isReceipt ? 'Receipt' : row.documentType || '',
          row.debitAmount > 0 ? Number(row.debitAmount) : 0,
          row.creditAmount > 0 ? Number(row.creditAmount) : 0,
          Number(row.runningBalance) || 0,
          row.overDueDays > 0 ? row.overDueDays : 0,
        ];
      }),
      ['', '', '', '', '', '', '', 'Current Balance', Number(currentBalance) || 0, ''],
    ];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Ledger');
    XLSX.writeFile(wb, `Ledger_${username}_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const headers = [
    'S.No', 'Document No', 'Posting Date', 'Customer Name', 'Lab ID',
    'Document Type', 'Debit', 'Credit', 'Running Balance', 
    'Overdue Days', 'Download',
  ];

  if (loading) {
    return (
      <Box sx={{ mt: 10, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'white', py: 4 }}>
      <Container maxWidth="xl">
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{ color: '#00024c' }}
          >
            Back
          </Button>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#00024c', flexGrow: 1 }}>
            Ledger
          </Typography>
          {data.length > 0 && (
            <Button
              variant="contained"
              color="success"
              startIcon={<TableChartIcon />}
              onClick={handleExportExcel}
            >
              Export Excel
            </Button>
          )}
        </Box>

        {error ? (
          <Alert severity="error">{error}</Alert>
        ) : data.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">No ledger entries found.</Typography>
          </Paper>
        ) : (
          <TableContainer component={Paper} elevation={2}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#00024c' }}>
                  {headers.map((h) => (
                    <TableCell key={h} sx={{ color: 'white', fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.map((row, index) => {
                  const isInvoice = row.documentType === 'Invoice';
                  const isReceipt = (row.documentNo || '').startsWith('RV/');

                  return (
                    <TableRow key={row.EntryNo} sx={{ bgcolor: index % 2 === 0 ? '#f5f5f5' : 'white' }}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>{row.documentNo}</TableCell>
                      <TableCell>{formatDate(row.postingDate)}</TableCell>
                      <TableCell>{row.customerName}</TableCell>
                      <TableCell>{row.labId}</TableCell>
                      <TableCell>
                        <Chip
                          label={isInvoice ? 'Invoice' : isReceipt ? 'Receipt' : row.documentType || '—'}
                          size="small"
                          color={isInvoice ? 'primary' : isReceipt ? 'success' : 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell sx={{ color: row.debitAmount > 0 ? '#d32f2f' : 'inherit' }}>
                        {row.debitAmount > 0 ? formatCurrency(row.debitAmount) : '—'}
                      </TableCell>
                      <TableCell sx={{ color: row.creditAmount > 0 ? '#2e7d32' : 'inherit' }}>
                        {row.creditAmount > 0 ? formatCurrency(row.creditAmount) : '—'}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>{formatCurrency(row.runningBalance)}</TableCell>
                      <TableCell>
                        {row.overDueDays > 0 ? (
                          <Chip
                            label={`${row.overDueDays} days`}
                            size="small"
                            color={row.overDueDays > 30 ? 'error' : 'warning'}
                          />
                        ) : '—'}
                      </TableCell>
                      <TableCell>
                        {isInvoice && (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            disabled={downloadingInvoice === row.documentNo}
                            onClick={() => handleDownloadInvoice(row.documentNo)}
                            sx={{ fontSize: '0.7rem', mr: 0.5 }}
                          >
                            {downloadingInvoice === row.documentNo ? 'Loading...' : 'Invoice'}
                          </Button>
                        )}
                        {isReceipt && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="success"
                            startIcon={<DownloadIcon />}
                            disabled={downloadingReceipt === row.documentNo}
                            onClick={() => handleDownloadReceipt(row.documentNo)}
                            sx={{ fontSize: '0.7rem' }}
                          >
                            {downloadingReceipt === row.documentNo ? 'Loading...' : 'Receipt'}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
                <TableRow sx={{ bgcolor: '#e3f2fd' }}>
                  <TableCell colSpan={8} sx={{ fontWeight: 700, textAlign: 'right', fontSize: '1rem' }}>
                    Current Balance:
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '1.1rem', color: currentBalance > 0 ? '#d32f2f' : '#2e7d32' }}>
                    {formatCurrency(currentBalance)}
                  </TableCell>
                  <TableCell colSpan={4} />
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Container>
    </Box>
  );
};

export default LedgerPage;